import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listAcademics = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId?: string; studentId?: string }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const [terms, subjects, timetable, assignments, grades, reports] = await Promise.all([
      supabase.from("academic_terms").select("*").order("start_date", { ascending: false }),
      supabase.from("subjects").select("*").order("name"),
      supabase.from("timetable_slots").select("*, subjects(name,color_hex)").order("day_of_week"),
      supabase.from("assignments").select("*, subjects(name,color_hex)").order("due_at", { ascending: false }).limit(30),
      data.studentId
        ? supabase.from("gradebook_entries").select("*, subjects(name)").eq("student_id", data.studentId).order("recorded_at", { ascending: false }).limit(50)
        : { data: [] },
      data.studentId
        ? supabase.from("report_cards").select("*").eq("student_id", data.studentId).order("published_at", { ascending: false }).limit(5)
        : { data: [] },
    ]);
    return {
      terms: terms.data ?? [], subjects: subjects.data ?? [], timetable: timetable.data ?? [],
      assignments: assignments.data ?? [], grades: (grades as any).data ?? [], reports: (reports as any).data ?? [],
    };
  });

export const seedAcademicsDemo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { count } = await supabase.from("subjects").select("id", { head: true, count: "exact" }).eq("school_id", data.schoolId);
    if ((count ?? 0) > 0) return { seeded: false };
    const subjectsPayload = [
      { code: "MATH", name: "Mathematics", color_hex: "#7B1416" },
      { code: "ENG", name: "English", color_hex: "#A42022" },
      { code: "SCI", name: "Science", color_hex: "#2C6E91" },
      { code: "SST", name: "Social Studies", color_hex: "#C4A882" },
      { code: "IT", name: "Computer Studies", color_hex: "#7A3C7A" },
      { code: "LIT", name: "Literature", color_hex: "#3A6E4B" },
    ].map(s => ({ ...s, school_id: data.schoolId }));
    const { data: subs } = await supabase.from("subjects").insert(subjectsPayload).select();
    if (!subs) return { seeded: false };

    const termStart = new Date(); termStart.setMonth(termStart.getMonth() - 1);
    const termEnd = new Date(); termEnd.setMonth(termEnd.getMonth() + 2);
    const { data: term } = await supabase.from("academic_terms").insert({
      school_id: data.schoolId, name: "Term 2 2026", start_date: termStart.toISOString().slice(0,10),
      end_date: termEnd.toISOString().slice(0,10), is_current: true,
    }).select().single();

    const slots: any[] = [];
    ["S2 · Boarding", "P4 · Day", "P1 · Day"].forEach(cls => {
      for (let d = 1; d <= 5; d++) {
        subs.slice(0, 5).forEach((s: any, i: number) => {
          const startH = 8 + i;
          slots.push({
            school_id: data.schoolId, class_label: cls, subject_id: s.id,
            teacher_name: ["Mr Otim","Mrs Nabbosa","Ms Alupo","Mr Bwire","Mrs Kirunda"][i],
            day_of_week: d, start_time: `${String(startH).padStart(2,'0')}:00`,
            end_time: `${String(startH).padStart(2,'0')}:40`, room: `Rm ${100 + i}`,
          });
        });
      }
    });
    await supabase.from("timetable_slots").insert(slots);

    const { data: students } = await supabase.from("students").select("id,class_label").eq("school_id", data.schoolId).limit(6);
    if (students) {
      const asgs: any[] = students.slice(0, 3).flatMap((st: any) =>
        subs.slice(0, 3).map((sub: any, i: number) => ({
          school_id: data.schoolId, subject_id: sub.id, class_label: st.class_label,
          title: `${sub.name}: Exercise ${i + 1}`, description: "Complete pages 12-15",
          due_at: new Date(Date.now() + (i + 1) * 3 * 864e5).toISOString(), max_score: 100,
        }))
      );
      await supabase.from("assignments").insert(asgs);

      const grades: any[] = students.flatMap((st: any) =>
        subs.map((sub: any) => ({
          school_id: data.schoolId, student_id: st.id, subject_id: sub.id,
          term_id: term?.id, assessment_label: "Mid-term test",
          score: 55 + Math.floor(Math.random() * 40), max_score: 100,
          teacher_comment: "Good progress",
        }))
      );
      await supabase.from("gradebook_entries").insert(grades);
    }

    return { seeded: true };
  });
